'use client';

import React from 'react';
import { usePathname } from 'next/navigation';
import { useAuth } from '@/hooks/useAuth';
import { permissionGuardService } from '@/shared/permissions';
import { UserRole, getUserRoleFromString } from '@/shared/permissions';
import { Logger } from '@/lib/logger';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, Loader2, Lock } from 'lucide-react';

/**
 * Static route permission check using the consolidated navigation system
 * This provides instant permission checks without database calls
 */
function checkStaticRoutePermission(pathname: string, userRoleString?: string | null): boolean {
  const requestId = Math.random().toString(36).substring(2, 12);
  
  Logger.info('PermissionGuard', 'Starting static route permission check', {
    pathname,
    userRoleString,
    requestId
  });

  // If no role, default to allowing common paths
  if (!userRoleString) {
    const publicPaths = ['/home', '/login', '/error', '/auth'];
    const isPublic = publicPaths.some(path => pathname.startsWith(path));
    
    Logger.info('PermissionGuard', 'No user role, checking public paths', {
      pathname,
      isPublic,
      requestId
    });
    
    return isPublic;
  }

  // Get user role from string
  const userRole = getUserRoleFromString(userRoleString);
  if (!userRole) {
    Logger.warn('PermissionGuard', 'Invalid user role string, denying access', {
      pathname,
      userRoleString,
      requestId
    });
    return false;
  }

  // Use the consolidated navigation service for static permission check
  try {
    const { navigationService } = require('@/shared/navigation');
    const hasAccess = navigationService.hasStaticUrlPermission(userRole, pathname);

    Logger.info('PermissionGuard', 'Static route permission check completed', {
      pathname,
      userRole,
      hasAccess,
      requestId
    });

    return hasAccess;
  } catch (error) {
    Logger.apiError('PermissionGuard', 'Error in static route permission check', error as Error, {
      method: 'STATIC_CHECK',
      action: 'checkStaticRoutePermission',
      requestId,
      userId: userRole.toString(),
      error: error instanceof Error ? error.stack : String(error)
    });
    return false;
  }
}

interface PermissionGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showRetry?: boolean;
  requireAuth?: boolean;
  requireUrlPermission?: boolean; // New prop to enable URL-based permission checking
}

export function PermissionGuard({
  children,
  fallback,
  showRetry = true,
  requireAuth = true,
  requireUrlPermission = true // Default to true for RouteGuard behavior
}: PermissionGuardProps) {
  const authContext = useAuth();
  const pathname = usePathname();
  const [urlPermissionCheck, setUrlPermissionCheck] = React.useState<{
    isLoading: boolean;
    hasPermission: boolean;
    error?: string;
  }>({
    isLoading: requireUrlPermission,
    hasPermission: !requireUrlPermission,
    error: undefined
  });

  // Check URL-based permissions when auth context is ready
  React.useEffect(() => {
    if (!requireUrlPermission) {
      return;
    }

    if (authContext.isLoading || authContext.error || !authContext.isEnhanced) {
      return;
    }

    const checkUrlPermission = async () => {
      try {
        setUrlPermissionCheck({ isLoading: true, hasPermission: false });
        
        Logger.info('PermissionGuard', 'Checking static navigation permission for user', {
          pathname,
          authContext: {
            userId: authContext.userId,
            orgId: authContext.orgId,
            role: authContext.role,
            isEnhanced: authContext.isEnhanced
          }
        });

        // Use static navigation-based permission check for instant results
        const hasPermission = checkStaticRoutePermission(pathname, authContext.role);
        
        Logger.info('PermissionGuard', 'Static navigation permission check completed', {
          pathname,
          userRole: authContext.role,
          hasPermission
        });
        
        setUrlPermissionCheck({ 
          isLoading: false, 
          hasPermission,
          error: hasPermission ? undefined : `Access denied to ${pathname}`
        });
      } catch (error) {
        Logger.error('PermissionGuard', 'Static navigation permission check failed', error);
        setUrlPermissionCheck({ 
          isLoading: false, 
          hasPermission: false,
          error: error instanceof Error ? error.message : 'Permission check failed'
        });
      }
    };

    checkUrlPermission();

    // Add timeout to prevent infinite loading states
    const timeoutId = setTimeout(() => {
      if (urlPermissionCheck.isLoading) {
        Logger.warn('PermissionGuard', 'Permission check timed out, redirecting to login', {
          pathname,
          authContext: {
            userId: authContext.userId,
            orgId: authContext.orgId,
            isEnhanced: authContext.isEnhanced
          }
        });
        
        // Redirect to login on timeout
        window.location.href = '/login?reason=permission_timeout';
      }
    }, 10000); // 10 second timeout

    return () => clearTimeout(timeoutId);
  }, [authContext.isLoading, authContext.isEnhanced, authContext.error, authContext.userId, authContext.orgId, authContext.role, pathname, requireUrlPermission, urlPermissionCheck.isLoading]);

  // Show loading state
  if (authContext.isLoading || urlPermissionCheck.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="flex items-center gap-2">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>
            {authContext.isLoading ? 'Loading session...' : 'Checking permissions...'}
          </span>
        </div>
      </div>
    );
  }

  // Show auth error state with retry option
  if (authContext.error) {
    // Check if this is a session expiration or authentication error that requires login redirect
    const isSessionError = authContext.error.includes('Session expired') ||
                          authContext.error.includes('Invalid session') ||
                          authContext.error.includes('User not authenticated') ||
                          authContext.error.includes('Authentication required') ||
                          authContext.error.includes('Please log in again');

    if (isSessionError) {
      // For session errors, redirect to login immediately
      if (typeof window !== 'undefined') {
        window.location.href = '/login?reason=session_expired';
      }
      return (
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="flex items-center gap-2">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span>Redirecting to login...</span>
          </div>
        </div>
      );
    }

    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <CardTitle className="text-red-900">
              Authentication Error
            </CardTitle>
            <CardDescription>
              {authContext.error}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-2">
            {showRetry && authContext.retry && (
              <Button 
                variant="outline" 
                onClick={authContext.retry}
                className="flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Retry
              </Button>
            )}
            <Button 
              variant="default" 
              onClick={() => window.location.href = '/login'}
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check authentication requirement
  if (requireAuth && !authContext.isEnhanced) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-orange-100">
              <AlertTriangle className="h-6 w-6 text-orange-600" />
            </div>
            <CardTitle>Authentication Required</CardTitle>
            <CardDescription>
              Please complete authentication to access this content.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Check URL permission requirement
  if (requireUrlPermission && !urlPermissionCheck.hasPermission) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <Lock className="h-6 w-6 text-red-600" />
            </div>
            <CardTitle className="text-red-900">
              Access Denied
            </CardTitle>
            <CardDescription>
              You don't have permission to access this page.
              {urlPermissionCheck.error && (
                <div className="mt-2 text-sm text-red-600">
                  {urlPermissionCheck.error}
                </div>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button 
              variant="outline" 
              onClick={() => window.history.back()}
              className="mr-2"
            >
              Go Back
            </Button>
            <Button 
              variant="default" 
              onClick={() => window.location.href = '/home'}
            >
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Return content if all checks pass
  return <>{children}</>;
}

// Legacy components maintained for compatibility

/**
 * Simple permission check component for inline usage
 */
interface PermissionCheckProps {
  url?: string;
  module?: string;
  action?: string;
  feature?: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export function PermissionCheck({
  url,
  module,
  action = 'read',
  feature,
  children,
  fallback = null
}: PermissionCheckProps) {
  const authContext = useAuth();

  // Simple check based on auth context
  if (authContext.isLoading) {
    return <div className="animate-pulse">Loading...</div>;
  }

  const hasPermission = authContext.isEnhanced && !authContext.error;
  return hasPermission ? <>{children}</> : <>{fallback}</>;
}

/**
 * Permission badge component for displaying permission status
 */
interface PermissionBadgeProps {
  url: string;
  showDetails?: boolean;
}

export function PermissionBadge({ url, showDetails = false }: PermissionBadgeProps) {
  const authContext = useAuth();

  if (authContext.isLoading) {
    return <span className="text-xs text-gray-500">Checking...</span>;
  }

  const hasPermission = authContext.isEnhanced && !authContext.error;

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
      hasPermission
        ? 'bg-green-100 text-green-800'
        : 'bg-red-100 text-red-800'
    }`}>
      {hasPermission ? '✓ Allowed' : '✗ Denied'}
      {showDetails && (
        <span className="ml-1 text-xs opacity-75">
          ({url})
        </span>
      )}
    </span>
  );
}

// For backward compatibility and specialized route protection
export function RouteGuard({ children }: { children: React.ReactNode }) {
  return (
    <PermissionGuard 
      requireAuth={true} 
      requireUrlPermission={true}
      showRetry={true}
    >
      {children}
    </PermissionGuard>
  );
}
